import { useState } from "react";
import { WE_RIDER_LOGO_URL as weRiderLogo } from "../../services/local/institutionalBranding";
import WeRiderAndroidLauncher from "./WeRiderAndroidLauncher";
import {
  isUsingLocalWeRiderAuth,
  requestWeRiderPasswordReset,
  signInWeRider,
  signUpWeRider,
} from "../../services/supabase/weRiderService";

const INITIAL_REGISTRATION = {
  fullName: "",
  email: "",
  phone: "",
  vehicleType: "",
  vehicleReference: "",
  password: "",
  confirmation: "",
  acceptedTerms: false,
};

const VEHICLE_OPTIONS = [
  { value: "motorcycle", label: "Moto" },
  { value: "bicycle", label: "Bicicleta" },
  { value: "scooter", label: "Scooter / monopatín" },
  { value: "car", label: "Auto" },
  { value: "walking", label: "A pie" },
  { value: "other", label: "Otro medio sin licencia" },
];

export default function WeRiderAccess({ onAuthenticated }) {
  const [mode, setMode] = useState("login");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [registration, setRegistration] = useState(INITIAL_REGISTRATION);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const callbackUrl = `${window.location.origin}/we-pedidos/rider/auth/callback`;

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await signInWeRider(login);
      onAuthenticated();
    } catch (requestError) {
      setError(requestError?.message || "No pudimos iniciar sesión.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (registration.password !== registration.confirmation) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!registration.acceptedTerms) {
      setError("Debes aceptar los términos y la política de privacidad.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signUpWeRider({
        ...registration,
        redirectTo: callbackUrl,
      });
      if (result.requiresEmailConfirmation) {
        setMessage(
          "Te enviamos un correo para confirmar la cuenta. Ábrelo para entrar al panel rider."
        );
      } else {
        onAuthenticated();
      }
    } catch (requestError) {
      setError(requestError?.message || "No pudimos crear tu cuenta de rider.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    const email = login.email.trim();
    if (!email) {
      setError("Primero ingresa el correo de tu cuenta.");
      return;
    }
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const result = await requestWeRiderPasswordReset({
        email,
        redirectTo: `${callbackUrl}?mode=recovery`,
      });
      setMessage(
        result.localPreview
          ? "La cuenta local sigue disponible con la contraseña que registraste."
          : "Te enviamos un correo para recuperar la contraseña."
      );
    } catch (requestError) {
      setError(requestError?.message || "No pudimos enviar el correo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="we-rider-access">
      <section className="we-rider-access-brand" aria-label="We! Rider">
        <img src={weRiderLogo} alt="We! Rider" />
        <span className="we-rider-role-badge">Riders</span>
        <div>
          <p className="we-rider-kicker">Riders de We! pedidos</p>
          <h1>Tu ruta de entregas</h1>
          <p className="we-rider-brand-copy">
            Recibe viajes, retira pedidos y confirma cada entrega desde el celular.
          </p>
        </div>
      </section>

      <section className="we-rider-access-panel" aria-labelledby="we-rider-access-title">
        <div className="we-rider-mobile-brand">
              <img src={weRiderLogo} alt="" />
          <div><strong>We! Rider</strong><span>Repartos</span></div>
        </div>
        <p className="we-rider-eyebrow">Acceso de repartidores</p>
        <h2 id="we-rider-access-title">
          {mode === "login" ? "Ingresá a tu turno" : "Registrate como rider"}
        </h2>

        <div className="we-rider-access-tabs" role="tablist" aria-label="Acceso del rider">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={mode === "login" ? "is-active" : ""}
            onClick={() => chooseMode("login")}
          >
            Ingresar
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={mode === "register" ? "is-active" : ""}
            onClick={() => chooseMode("register")}
          >
            Crear cuenta
          </button>
        </div>

        {mode === "login" ? (
          <form className="we-rider-form" onSubmit={handleLogin}>
            <label>
              Correo electrónico
              <input
                type="email"
                autoComplete="email"
                value={login.email}
                onChange={(event) => setLogin({ ...login, email: event.target.value })}
                placeholder="rider@correo.com"
                required
              />
            </label>
            <label>
              Contraseña
              <span className="we-rider-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={login.password}
                  onChange={(event) => setLogin({ ...login, password: event.target.value })}
                  placeholder="Tu contraseña"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? "Ocultar" : "Ver"}
                </button>
              </span>
            </label>
            <button
              type="button"
              className="we-rider-link-button"
              onClick={handlePasswordReset}
              disabled={submitting}
            >
              Olvidé mi contraseña
            </button>
            <button className="we-rider-primary" type="submit" disabled={submitting}>
              {submitting ? "Ingresando..." : "Ingresar"}
            </button>
          </form>
        ) : (
          <form className="we-rider-form" onSubmit={handleRegister}>
            <label>
              Nombre y apellido
              <input
                autoComplete="name"
                value={registration.fullName}
                onChange={(event) =>
                  setRegistration({ ...registration, fullName: event.target.value })
                }
                placeholder="Ej. Diego Portillo"
                required
              />
            </label>
            <div className="we-rider-form-row">
              <label>
                Correo electrónico
                <input
                  type="email"
                  autoComplete="email"
                  value={registration.email}
                  onChange={(event) =>
                    setRegistration({ ...registration, email: event.target.value })
                  }
                  placeholder="rider@correo.com"
                  required
                />
              </label>
              <label>
                Celular
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={registration.phone}
                  onChange={(event) =>
                    setRegistration({ ...registration, phone: event.target.value })
                  }
                  placeholder="379 4123456"
                  required
                />
              </label>
            </div>
            <div className="we-rider-form-row">
              <label>
                Vehículo
                <select
                  value={registration.vehicleType}
                  onChange={(event) =>
                    setRegistration({ ...registration, vehicleType: event.target.value })
                  }
                  required
                >
                  <option value="" disabled>Selecciona tu medio</option>
                  {VEHICLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Identificación del vehículo
                <input
                  value={registration.vehicleReference}
                  onChange={(event) =>
                    setRegistration({
                      ...registration,
                      vehicleReference: event.target.value,
                    })
                  }
                  placeholder="Patente o color"
                />
              </label>
            </div>
            <div className="we-rider-form-row">
              <label>
                Contraseña
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={registration.password}
                  onChange={(event) =>
                    setRegistration({ ...registration, password: event.target.value })
                  }
                  placeholder="Mínimo 8 caracteres"
                  required
                />
              </label>
              <label>
                Repetir contraseña
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  minLength={8}
                  value={registration.confirmation}
                  onChange={(event) =>
                    setRegistration({ ...registration, confirmation: event.target.value })
                  }
                  placeholder="Repetila"
                  required
                />
              </label>
            </div>
            <label className="we-rider-terms">
              <input
                type="checkbox"
                checked={registration.acceptedTerms}
                onChange={(event) =>
                  setRegistration({
                    ...registration,
                    acceptedTerms: event.target.checked,
                  })
                }
              />
              <span>Acepto los términos de uso y la política de privacidad.</span>
            </label>
            <button
              type="button"
              className="we-rider-link-button"
              onClick={() => setShowPassword((current) => !current)}
            >
              {showPassword ? "Ocultar contraseñas" : "Mostrar contraseñas"}
            </button>
            <button className="we-rider-primary" type="submit" disabled={submitting}>
              {submitting ? "Creando cuenta..." : "Crear cuenta rider"}
            </button>
          </form>
        )}

        {error ? <p className="we-rider-message is-error" role="alert">{error}</p> : null}
        {message ? <p className="we-rider-message is-success" role="status">{message}</p> : null}
        {isUsingLocalWeRiderAuth() ? (
          <div className="we-rider-demo-access">
            <span>Cuenta de prueba local</span>
            <strong>rider.prueba@we.local</strong>
            <code>Prueba1234</code>
          </div>
        ) : null}
        <WeRiderAndroidLauncher />
      </section>
    </main>
  );
}
